/*
* Created on Mar 08, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import ic_revoke_svg from "../../static/img/utils/ic-revoke.svg";
import ic_assign_svg from "../../static/img/utils/ic-assign.svg";
import ic_resend_svg from "../../static/img/utils/ic-resend.svg";
import ic_unsuspend_svg from "../../static/img/utils/ic-unsuspend.svg";
import ic_more_svg from "../../static/img/utils/ic-more.svg";
import {dataCellLabelToDataGridArea, disableAccountCheckBox, normalizeSpaceSize} from "../../service/utils";
import UserAvatarIcon from "../common/UserAvatarIcon";
import React from "react";
import {getTimeWithTimezone} from "../../service/timezone";
import useToggle from "../../hook/ToggleHook";
import {useParams} from "react-router-dom";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useAlertContext} from "../../context/AlertContext";

const AccountListTableRowComponent = ({ row_data, index, doRefresh, disPatcher, translate:t, isOwner, permissions=null, show_checkbox=false, table_head_fields, state}) => {
    const {oid} = useParams();
    const {OrganizationAPI} = useOrganizationApi();
    const alert = useAlertContext()
    const groups = row_data?.groups || [];
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const [open, node] = useToggle();
    const siteMap = Array.isArray(state?.sites)
    ? state.sites.reduce((map, site) => {
        map.set(site.id, site.name);
        return map;
    }, new Map())
    : new Map();
    const deviceMap = state?.devices?.reduce((map, device) => {
        map.set(device.id, device.name);
        return map;
    }, new Map());
    const get_status = () => {
        const status = row_data.status;
        switch (status) {
            case 'active':
                return ['Activate', 'status-color-green'];
            case 'pending':
                return ['Invited', 'status-color-royal-blue'];
            case 'suspend':
                return ['Suspended', 'status-color-red'];
            // case '' || null || undefined:
            //     return ['QNAP ID not activated', 'status-color-purple'];
            default:
                return ['QID not activated', 'status-color-purple'];
        }
    }

    // Process quota usages by region
    // If the user has seated regions but no quota usages, initialize usage to 0 for each seated region
    // Otherwise, aggregate the usage by region
    const quota_usages = row_data?.seated_regions && ! row_data?.quota_usages ?
        new Map(Object.keys(row_data?.seated_regions).map(key => [key, {usage: 0, region: key}]))?.values()?.toArray()?.sort((a, b) => a.region.localeCompare(b.region))
        : row_data?.quota_usages?.reduce((regionMap, region)=>{
        if (regionMap.get(region.region)) {
            regionMap.set(region.region, {
                region: region.region,
                usage: regionMap.get(region.region).usage + region.usage
            })
        }
        return regionMap;
    }, new Map(Object.keys(row_data?.seated_regions || []).map(key => [key, {usage: 0, region: key}])))
        ?.values()?.toArray()?.sort((a, b) => a.region.localeCompare(b.region));

    const showFirstActionIcon = () => {
        if (row_data.status === 'suspend') {
            return (
                <div className="tooltip">
                    <img src={ic_unsuspend_svg} alt="" />
                    <span className="tooltiptext !transform !top-100 !left-100 !-translate-x-80">{t('@Unsuspend@')}</span>
                </div>
            )
        } else if (['pending', 'qid_not_activated'].includes(row_data.status)) {
            return (
                <div className="tooltip">
                    <img src={ic_resend_svg} alt="" />
                    <span className="tooltiptext !transform !top-100 !left-100 !-translate-x-80">{t('@Resend Invitation@')}</span>
                </div>
            )
        } else if (row_data.status === 'active') {
            return (
                <div className="tooltip">
                    <img src={ic_assign_svg} alt="" />
                    <span className="tooltiptext !transform !top-100 !left-100 !-translate-x-80">{t('@Assign Role and Permission@')}</span>
                </div>
            )
        }
    }

    const showRole = () => {
        if (row_data.is_owner) {
            return t('@Owner@');
        } else if (row_data.role) {
            const roles = {
                owner: '@Owner@',
                user_manager: '@User Manager@',
                member: '@Member@'
            };
            return t(`${roles[row_data.role]}`);
        } else {
            return t('@Member@');
        }
    }

    const showDate= () => {
        let date_string = 'joined at';
        let date = row_data?.created_at;
        if (['active', 'suspend'].includes(row_data?.status)) {
            date = row_data?.accepted_at || row_data?.created_at
        } else {
            date_string = 'invited at';
        }
        date = getTimeWithTimezone(date, false);
        return (
            <>
                <div className="fs12 color-gray">{t(`@${date_string}@`)}</div>
                <div>{date}</div>
            </>
        );
    }
    showDate();

    const currentUserRole = state?.currentUserRole;
    const isUserManager = currentUserRole === 'user_manager';
    const isTargetOwner = row_data.is_owner || row_data.role === 'owner';
    const lockUser = disableAccountCheckBox(row_data, currentUser, isOwner, permissions, currentUserRole);

    const showUpdate = () => {
        // User Manager cannot operate on Owner users
        if (isUserManager && isTargetOwner) return false;
        if (isOwner) return true;

        const has_permission_and_owner = permissions?.invite_admin && row_data.is_owner;
        if (has_permission_and_owner) return false

        const is_self = row_data.id === currentUser.user_id;
        const has_permission_unmatch_domain = () => {
            const target_email = row_data.email || row_data.qid;
            const curUser_email = currentUser.emails[0].email;
            if (permissions?.invite_admin) {
                return target_email.split('@')[1].toLowerCase() === curUser_email.split('@')[1].toLowerCase();
            }
            return false;
        }
        return is_self || has_permission_unmatch_domain();
    }

    const [status, statusCss] = get_status();
    const group_size = groups ? groups.length : 0;

    const toggle_checkbox = (row) => {
        disPatcher({type:'toggle_checkbox', payload: row});
    }

    const resendInvit = async () => {
        try {
            await OrganizationAPI.post(`organization_invitation/organization/${oid}/resend`,
            null, {qids: [row_data?.email || row_data?.qid]})
            alert(true, 'success', 0, `${t("@An email has been sent to@")} ${row_data?.email || row_data?.qid}`, 10 * 1000);
        } catch (err) {
            console.log(err);
            alert(true, 'error', err.response.data.code, err.response.data.message);
        }
    }

    const toggleAction = (isShow) => {
        if (row_data.status === 'suspend') {
            disPatcher({type: 'toggle_show_suspend', payload: {showSuspend: isShow, row_data}});
        } else if (['pending', 'inactivate', 'qid_not_activated'].includes(row_data.status)) {
            resendInvit();
        } else if (row_data.status === 'active') {
            disPatcher({type: 'toggle_show_permissions', payload: {showPermissions: isShow, row_data}});
        }
    }

    const handleOnDeleteClick = () => {
        if (row_data.id === currentUser.user_id) {
            disPatcher({type: 'open_leave_alert', payload: true, row_data});
        }
        else {
            disPatcher({type: 'toggle_checkbox', payload: {id: row_data.id}, row_data});
            disPatcher({type: 'open_alert_delete_account_list', payload: {id: row_data.id}, row_data});
        }

    }

    return (
        <div className="flexbox-trow" key={row_data.id}>
            <div className="tbody-cell select-cell">
                <div className={`comp-checkbox no-label ${show_checkbox ? 'visible-checkbox' : ''}`}>
                    <input type="checkbox" id={`cb-${index}`}
                           checked={row_data.checked || false}
                           onChange={()=>toggle_checkbox(row_data)}
                           disabled={lockUser}
                    />
                        <label htmlFor={`cb-${index}`} className="comp-label" />
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[0].width_style}`}
                 data-cell-label={t(table_head_fields[0].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[0].field_name)}>
                <div className="account-info-group">
                    <UserAvatarIcon user_info={row_data} with_user_info={true} avatar_class='admin'/>
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[1].width_style}`}
                 data-cell-label={t(table_head_fields[1].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[1].field_name)}>
                <div className={`status-tag ${statusCss} no-border !pl-0 !pr-0 disable-event`}>
                    <span className="label">{`${t(`@${status}@`)}`}</span>
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[2].width_style}`}
                 data-cell-label={t(table_head_fields[2].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[2].field_name)}>
                {showRole()}
            </div>
            <div className={`tbody-cell ${table_head_fields[3].width_style}`}
                 data-cell-label={t(table_head_fields[3].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[3].field_name)}>
                {!quota_usages && '-'}
                {
                    quota_usages?.length > 0 &&
                    <div className="comp-storage-usage">
                        <div className="usage-value-region">
                            <div className="usage-value-region__amount">
                                {normalizeSpaceSize(
                                    quota_usages?.reduce((total, region) => {
                                        return total + (region?.usage || 0);
                                    }, 0)
                                )}
                            </div>
                            <div className="tag-group">
                                <span className="tag">
                                    {`${quota_usages?.length} ${t('@regions@')}`}
                                </span>
                            </div>
                        </div>
                        <div className="popover">
                            <div className="popover-menu">
                                {
                                    quota_usages.map((region, index) => (
                                        <div className="usage-amount__hint" key={index}>
                                            <span className="usage-amount__hint__marker usage-amount__hint__marker--default"></span>
                                            <span className="usage-amount__hint__label">{t(`@${region.region}@`)}:</span>
                                            <span className="usage-amount__hint__value">{normalizeSpaceSize(region.usage)}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
            <div className={`tbody-cell ${table_head_fields[4].width_style}`}
                 data-cell-label={t(table_head_fields[4].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[4].field_name)}>
                {(() => {
                    // Permissions can come in inconsistent shapes per user:
                    //   manage_site: ["id", ...] (array — normal)
                    //               | {}           (empty object — granted-with-no-targets)
                    //               | undefined
                    //   manage_device: {user_assigned: [...], fixed: {site_id: [...]}}  (current)
                    //                | ["id", ...]                                      (legacy array)
                    //                | {}                                                (empty)
                    //                | undefined
                    // Normalise to flat id arrays before deciding what to render.
                    const ms = row_data?.permissions?.manage_site;
                    const rawSiteIds = Array.isArray(ms) ? ms : [];

                    const md = row_data?.permissions?.manage_device;
                    let rawDeviceIds = [];
                    if (Array.isArray(md)) {
                        rawDeviceIds = md;
                    } else if (md && typeof md === 'object') {
                        rawDeviceIds = (md.user_assigned || []).concat(
                            Object.values(md.fixed || {}).flat()
                        );
                    }

                    // Drop IDs whose site/device has been deleted from the org —
                    // otherwise the badge count outruns what the tooltip can name.
                    // Dedup: a device can be BOTH an individual whitelist grant (user_assigned)
                    // AND a site-cascade grant (fixed) for the same user — counting it twice
                    // inflates the badge and duplicates the tooltip entry. (manage_site is already
                    // unique, but dedup it too for consistency.)
                    const siteIds = [...new Set(rawSiteIds)].filter(id => siteMap.has(id));
                    const deviceIds = [...new Set(rawDeviceIds)].filter(id => deviceMap?.has(id));

                    const hasAnyBadge = siteIds.length > 0 || deviceIds.length > 0;

                    if (!hasAnyBadge) {
                        return '-';
                    }
                    return (
                        <div className="tag-group hover-tooltip">
                            {siteIds.length > 0 &&
                                <span className="tag tag--site">
                                    {siteIds.length} {t('@Sites@')}
                                </span>
                            }
                            {deviceIds.length > 0 &&
                                <span className="tag tag--device">
                                    {deviceIds.length}&nbsp;{t('@Devices@')}
                                </span>
                            }
                            <div className="popover">
                                <div className="popover-menu">
                                    {siteIds.length > 0 &&
                                        <div className="popover-menu__section popover-menu__section--site">
                                            <div className="fs12 color-gray mb-2">{t('@Site@')}</div>
                                            {siteIds.map(site => <div key={site}>{siteMap.get(site)}</div>)}
                                        </div>
                                    }
                                    {deviceIds.length > 0 &&
                                        <div className="popover-menu__section popover-menu__section--device">
                                            <div className="fs12 color-gray mb-2">{t('@Device@')}</div>
                                            {deviceIds.map(device => <div key={device}>{deviceMap.get(device)}</div>)}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
            <div className={`tbody-cell ${table_head_fields[5].width_style}`}
                 data-cell-label={t(table_head_fields[5].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[5].field_name)}>
                {showDate()}
            </div>
            <div className={`tbody-cell ${table_head_fields[6].width_style}`}
                 data-cell-label={t(table_head_fields[6].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[6].field_name)}>
                <div className="act-btn-group">
                    {!(isUserManager && isTargetOwner) &&
                        <div className="act-btn" data-action-type="Edit" onClick={() => toggleAction(true, row_data)}>
                            {showFirstActionIcon()}
                        </div>
                    }
                    {!(isUserManager && isTargetOwner) &&
                        <div className="act-btn" data-action-type="More" ref={node}>
                            <img src={ic_more_svg} alt=""/>
                            <div className={`comp-dropdown drop-align-right drop-adjustable ${open}`}>
                                <ul className="comp-dropdown-menu">
                                    <li className="section">
                                        { !['pending', 'qid_not_activated'].includes(row_data?.status) &&
                                            <>
                                            {!isUserManager && (
                                            <>
                                            <div className="section-items selection" onClick={() => disPatcher({type: 'toggle_show_share', payload: {showShareFolder: true, row_data}})}>{t('@Content Permission@')}</div>
                                            <div className="section-items selection" onClick={() => disPatcher({type: 'toggle_show_revoke', payload: {showRevoke: true, row_data}})}>{t('@Manage Space Access@')}</div>
                                            </>
                                            )}
                                            {row_data?.status !== 'suspend' && row_data.id !== currentUser.user_id &&
                                                <div className="section-items selection" onClick={() => disPatcher({type:'toggle_show_suspend', payload: {showSuspend: true, row_data}})}>{t('@Suspend@')}</div>
                                            }
                                            </>
                                        }
                                        <div className="section-items selection" onClick={handleOnDeleteClick}>{row_data.id === currentUser.user_id ? t('@Leave@') : t('@Delete@')}</div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default AccountListTableRowComponent;